/**
 * Monk Commerce capture-intercepts conventional checkout controls on this
 * storefront. The drawer therefore exposes a neutral purchase button and this
 * once-loaded delegated handler performs the final, validated navigation.
 * Delegation keeps the control working after Shopify morphs the cart markup.
 */

const SECURE_PURCHASE_SELECTOR = '[data-cronk-secure-purchase]';

/**
 * Return a same-origin Shopify checkout URL, or null for malformed/unexpected
 * destinations. A locale prefix (for example /en/checkout) is allowed.
 *
 * @param {string} rawUrl
 * @param {{ origin: string }} location
 * @returns {string | null}
 */
export function resolveSecurePurchaseUrl(rawUrl, location) {
  if (!rawUrl || !location?.origin) return null;

  try {
    const url = new URL(rawUrl, `${location.origin}/`);
    const pathSegments = url.pathname.split('/').filter(Boolean);

    if (url.origin !== location.origin || pathSegments.at(-1) !== 'checkout') {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
}

/**
 * @param {Event | { target?: { closest?: (selector: string) => HTMLButtonElement | null } }} event
 * @param {{ origin: string, assign: (url: string) => void }} location
 * @returns {boolean} Whether navigation was started.
 */
export function handleSecurePurchase(event, location) {
  const button = event.target?.closest?.(SECURE_PURCHASE_SELECTOR);

  if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') {
    return false;
  }

  const purchaseUrl = resolveSecurePurchaseUrl(button.dataset.purchaseUrl, location);
  if (!purchaseUrl) return false;

  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  try {
    location.assign(purchaseUrl);
    return true;
  } catch {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    return false;
  }
}

/**
 * Restore controls when a customer returns from the browser back-forward cache.
 *
 * @param {{ querySelectorAll: (selector: string) => Iterable<HTMLButtonElement> }} root
 */
export function resetSecurePurchaseControls(root) {
  for (const button of root.querySelectorAll(SECURE_PURCHASE_SELECTOR)) {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') {
  document.addEventListener(
    'click',
    (event) => handleSecurePurchase(event, window.location),
    { capture: true }
  );
  window.addEventListener('pageshow', () => resetSecurePurchaseControls(document));
}
